import { WorkflowDataSourceFieldDataType, FieldState, ValueType } from '@/types/enums';
import { ValidationModel } from '@/types/models/validation';
import { mapActions } from 'vuex';

import { Validator } from 'vee-validate';

Validator.extend('AlphaNumeric', {
  getMessage: (field: string) => 'The ' + field + ' must be an alphanumeric field.',
  validate: (value: string) => {
    const regexp = new RegExp(/^[A-Za-z0-9./-]*$/);
    return regexp.test(value);
  }
});

Validator.extend('Barcode', {
  getMessage: (field: string) => 'The ' + field + ' must be a valid barcode (must only contain letters, numbers and the following special characters: - .).',
  validate: (value: string) => {
    const regexp = new RegExp(/^[A-Za-z0-9.-]*$/);
    return regexp.test(value);
  }
});

Validator.extend('UserName', {
  getMessage: (field: string) => 'The user name may only contain letters, digits and the following special characters: @ _ + -',
  validate: (value: string) => {
    const regexp = new RegExp(/^[a-zA-Z0-9-._@+]*$/);
    return regexp.test(value);
  }
});

Validator.extend('MultipleEmail', {
  getMessage: (field: string) => `Incorrect email address format. Multiple addresses must be separated by semi-colons.`,
  validate: (value: string) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

    if (value.endsWith(';')) {
      return false;
    }

    const emails = value.split(';');

    return emails.every((email) => {
      const trimmedEmail = email.trim();
      return trimmedEmail && emailRegex.test(trimmedEmail);
    });
  }
});

// Do not use for numeric fields, only for objects where an id of 0 is to be treated as null
Validator.extend('notNullOrZero', {
  getMessage: (field: string) => 'The ' + field + ' must have a value.',
  validate: (value: number) => {
    return value && value > 0;
  }
});

Validator.extend('unique', {
  getMessage: (field: any) => `The ${field} value must be unique.`,
  validate: (value: string, { list }: { list: string[] }) => {
    return !value || !list || list.every((listItem: any) => listItem.trim().toLowerCase() !== value.trim().toLowerCase());
  }
});

Validator.extend('AtLeaseOneArrayElement', {
  getMessage: (field: string) => `At lease one ${field} must be selected.`,
  validate: (value: any) => {
    return value.length > 0;
  }
});

Validator.extend('lessThanField', {
  params: ['maxValueFieldName', 'maxValue'],
  validate: (value: number, { maxValue }: { maxValue: number }) => {
    return value < maxValue;
  },
  getMessage: (field: string, { maxValueFieldName }: { maxValueFieldName: string }) => `${field} field must be less than ${maxValueFieldName} field`
});

Validator.extend('MobileNumber', {
  getMessage: (field: string, params: string[], data: any) => {
    return data && data.message;
  },
  validate: (value: string) => {
    const regexp = new RegExp(/^([+])\d{1,14}$/);
    const mobileRegexResult: boolean = regexp.test(value);
    let errorMessage: string = '';
    if (!mobileRegexResult) {
      if (value.substring(0, 1) !== '+') {
        errorMessage = 'Mobile Number must start with "+".';
      } else if (value === '+') {
        errorMessage = '"+" must be followed by a mobile number.';
      } else {
        errorMessage = 'Invalid characters in mobile number. Must start with a + and only contain numbers.';
      }
    }
    return new Promise((resolve) => {
      resolve({
        valid: mobileRegexResult,
        data: { message: errorMessage }
      });
    });
  }
});

Validator.extend('Url', {
  getMessage: () => `Incorrect url format.`,
  validate: (value: string) => {
    const regexp = new RegExp(/^https?:\/\/[\w-]+(\.[.\w-]+)*(:[0-9]+)?\/?(\/[.\w-]*)*$/);
    return regexp.test(value);
  }
});

Validator.extend('WindowsFolderPath', {
  getMessage: () => `Invalid Windows folder path format.`,
  validate: (value: string) => {
    const windowsPathRegex = /^([a-zA-Z]:\\|\\\\[^\\]+\\[^\\]+)(\\[^\\/:*?"<>|]*)*\\?$/;
    const uncPathRegex = /^\\\\[^\\]+\\[^\\]+(\\[^\\/:*?"<>|]*)*\\?$/;
    const drivePathRegex = /^[a-zA-Z]:\\([^\\/:*?"<>|]+(\\)?)*$/;
    const regexp = new RegExp(`${windowsPathRegex.source}|${uncPathRegex.source}|${drivePathRegex.source}`);
    return regexp.test(value);
  }
});

export const WorkflowFormValidation = {
  methods: {
    getDataTypeValidationRule(dataType: WorkflowDataSourceFieldDataType): string {
      switch (dataType) {
        case WorkflowDataSourceFieldDataType.Integer:
          return '|integer';
        case WorkflowDataSourceFieldDataType.Decimal:
        case WorkflowDataSourceFieldDataType.Double:
          return '|decimal';
        case WorkflowDataSourceFieldDataType.String:
        case WorkflowDataSourceFieldDataType.Boolean:
        case WorkflowDataSourceFieldDataType.DateTime:
        case WorkflowDataSourceFieldDataType.Object:
        case WorkflowDataSourceFieldDataType.Unknown:
          return '';
      }
    }
  }
};

export const FormFieldValidation = {
  data() {
    return {
      _formDirty: false
    };
  },
  computed: {
    formDirty(this: any) {
      return this.isFormDirty() || this.$data._formDirty;
    },
    veeValidateRule(this: any) {
      const validationModel = this.validation as ValidationModel;
      if (!validationModel) {
        return null;
      }
      const veeValidateRule = {} as any;
      if (validationModel.valueType != null) {
        switch (validationModel.valueType) {
          case ValueType.AlphaNumeric:
            veeValidateRule.AlphaNumeric = true;
            break;
          case ValueType.Integer:
            veeValidateRule.integer = true;
            break;
          case ValueType.Decimal:
            veeValidateRule.decimal = true;
            break;
          case ValueType.Email:
            veeValidateRule.email = true;
            break;
          case ValueType.MultipleEmail:
            veeValidateRule.MultipleEmail = true;
            break;
          case ValueType.MobileNumber:
            veeValidateRule.MobileNumber = true;
            break;
          case ValueType.UserName:
            veeValidateRule.UserName = true;
            break;
          case ValueType.Url:
            veeValidateRule.Url = true;
            break;
          case ValueType.Barcode:
            veeValidateRule.Barcode = true;
            break;
        }
      }

      if (validationModel.required != null) {
        veeValidateRule.required = validationModel.required;
      }
      if (validationModel.minLength != null) {
        veeValidateRule.min = validationModel.minLength;
      }
      if (validationModel.maxLength) {
        veeValidateRule.max = validationModel.maxLength;
      }
      if (validationModel.fixedLength != null) {
        veeValidateRule.length = validationModel.fixedLength;
      }
      if (validationModel.minValue != null && validationModel.maxValue != null) {
        veeValidateRule.between = { min: validationModel.minValue, max: validationModel.maxValue };
      } else if (validationModel.minValue != null) {
        veeValidateRule.min_value = validationModel.minValue;
      } else if (validationModel.maxValue != null) {
        veeValidateRule.max_value = validationModel.maxValue;
      }
      if (validationModel.mustExistInList != null) {
        this.addItemMustExistInListRule();
        veeValidateRule.mustExistInList = { existsInList: this.existsInListIsValid };
      }
      if (validationModel.notNullOrZero) {
        veeValidateRule.notNullOrZero = true;
      }

      if (validationModel.regex != null) {
        veeValidateRule.regex = validationModel.regex;
      }

      if (validationModel.lessThanField != null) {
        veeValidateRule.lessThanField = validationModel.lessThanField;
      }

      if (validationModel.windowsFolderPath) {
        veeValidateRule.WindowsFolderPath = true;
      }

      if (validationModel.unique != null) {
        veeValidateRule.unique = { list: validationModel.unique };
      }

      return veeValidateRule;
    }
  },
  watch: {
    formDirty(this: any) {
      this.setApplicationDirty(this.formDirty);
    }
  },
  methods: {
    ...mapActions('applicationDirtyStore', ['setApplicationDirty', 'setApplicationDirtyOnModal']),
    getInputState(this: any, flags: any, errors: string[]) {
      if (flags && (flags.dirty || flags.validated)) {
        return !errors || errors.length === 0 ? null : false;
      }
      return null;
    },
    validateInputState(this: any, field: string, scope?: string) {
      const formField = this.getField(field, scope);
      if (formField && this.checkFieldDirtyOrValidated(formField)) {
        return !this.errors.has(field, scope) ? null : false;
      }
      return null;
    },
    getValidationInputClass(this: any, field: string, scope: string, baseClass?: string): string {
      const inputCssClass = baseClass === undefined ? '' : `${baseClass} `;
      const inputState = this.validateInputState(field, scope);

      if (inputState === null) {
        return inputCssClass;
      }

      return inputState ? `${inputCssClass}is-valid` : `${inputCssClass}is-invalid`;
    },
    getInputStateClass(this: any, flags: any, errors: string[], baseClass?: string): string {
      const inputCssClass = baseClass === undefined ? '' : `${baseClass} `;
      const inputState = this.getInputState(flags, errors);

      if (inputState === null) {
        return inputCssClass;
      }

      return inputState ? `${inputCssClass}is-valid` : `${inputCssClass}is-invalid`;
    },
    getRangeValidationRule(minimumValue: number, maximumValue: number): string {
      if (minimumValue && maximumValue) {
        return `|between:${minimumValue},${maximumValue}`;
      } else if (minimumValue && !maximumValue) {
        return `|min_value:${minimumValue}`;
      } else if (!minimumValue && maximumValue) {
        return `|max_value:${maximumValue}`;
      } else {
        return '';
      }
    },
    getMaxLengthValidationRule(this: any, maxLength: number): string {
      this.$validator.extend('max', {
        getMessage: (field: any, length: any) => `The ${field} field cannot be longer than ${length} characters`,
        validate: (value: any, length: any) => value.toString().length <= length
      });
      if (maxLength && maxLength !== 0) {
        return `|max:${maxLength}`;
      } else {
        return '';
      }
    },
    addItemMustExistInListRule(this: any) {
      this.$validator.extend('mustExistInList', {
        getMessage: (field: any) => `Please select an item from the list for field ${field}`,
        validate: (value: any, args: any) => {
          return args.existsInList;
        }
      });
    },
    isFormValid(this: any, scope?: string): boolean {
      return this.checkForm(FieldState.Valid, scope);
    },
    isFormDirty(this: any, scope?: string): boolean {
      return this.checkForm(FieldState.Dirty, scope);
    },
    hasFormChanged(this: any, scope?: string): boolean {
      return this.checkForm(FieldState.Changed, scope);
    },
    checkForm(this: any, action: FieldState, scope?: string) {
      const fields = scope ? this.$validator?.fields[scope] : this.$validator?.fields;
      if (!fields) {
        return false;
      }

      if (action === FieldState.Valid) {
        return Object.keys(fields.items).some((key) => this.checkFieldValid(fields.items[key]));
      } else if (action === FieldState.Dirty) {
        return Object.keys(fields.items).some((key) => this.checkFieldDirty(fields.items[key]));
      } else {
        return Object.keys(fields.items).some((key) => this.checkFieldChanged(fields.items[key]));
      }
    },
    checkFieldDirtyOrValidated(field: any) {
      return this.checkFieldDirty(field) || this.checkFieldValidated(field);
    },
    checkFieldValid(field: any) {
      return field.flags.validated && field.flags.valid;
    },
    checkFieldValidated(field: any) {
      return field.flags.validated;
    },
    checkFieldDirty(field: any) {
      return field.flags.dirty;
    },
    checkFieldChanged(field: any) {
      return field.flags.changed;
    },
    resetField(this: any, field: any, scope?: any) {
      const formField = this.getField(field, scope);
      if (formField) {
        formField.reset();
        this.$validator.errors.remove(formField.name, formField.scope);
      }
    },
    getField(this: any, formField: any, formScope?: any) {
      return this.$validator.fields.find({ name: formField, scope: formScope });
    },
    async validateFormAsync(this: any, formScope?: any) {
      try {
        return formScope ? this.$validator.validateAll(formScope) : this.$validator.validate();
      } catch (error) {
        this.$log('validator error ' + error);
        return false;
      }
    },
    addFormValidationError(this: any, errorMessage: string) {
      this.$validator.errors.add({
        field: FormFieldValidation.Constants.FormsValidationErrorsField,
        msg: errorMessage
      });
    },
    resetFormValidation(this: any) {
      this.$validator.errors.remove(FormFieldValidation.Constants.FormsValidationErrorsField);
    },
    setFocusOnInvalidField(this: any, component: any) {
      const element = component.$el.querySelector('.is-invalid') as HTMLInputElement;
      component.$nextTick(() => {
        if (element) {
          element.focus();
          if (typeof element.select === 'function') {
            element.select();
          }
        }
      });
    },
    applyValidationResult(this: any, result: any) {
      this.$refs.ValidationProvider.applyResult(result);
    }
  },
  Constants: {
    FormsValidationErrorsField: 'validationErrors'
  }
};
